import { RefObject, useCallback, useEffect, useRef, useState } from "react";

import {
    KeyboardMenuPlacement,
    KeyboardViewFit,
    needsKeyboardViewportCompensation,
    onKeyboardSettle,
    resolveKeyboardMenuPlacement,
    scrollIntoKeyboardView,
} from "../methods/keyboardViewport";

export type UseKeyboardAwareFieldOptions = KeyboardViewFit & {
    /**
     * Opt in for this field. Still no-ops on Android — compensation only runs on
     * platforms where `needsKeyboardViewportCompensation()` is true.
     */
    enabled?: boolean;
    /** Scroll the field above the keyboard on focus (iOS only). */
    scrollOnFocus?: boolean;
    /** Enables keyboard-aware `menuPlacement` (iOS only). */
    menuMaxHeight?: number;
    defaultMenuPlacement?: KeyboardMenuPlacement;
};

const FIELD_INPUT_SELECTOR = 'input:not([type="hidden"])';
const noopFocus = () => {};

/**
 * iOS: keeps a focused field above the keyboard and resolves dropdown direction
 * from the visible viewport.
 *
 * Android: returns inert handlers — the browser already handles keyboard scroll.
 */
export function useKeyboardAwareField(
    anchorRef: RefObject<HTMLElement | null>,
    {
        enabled = true,
        scrollOnFocus = true,
        menuMaxHeight,
        defaultMenuPlacement = "bottom",
        clearance,
    }: UseKeyboardAwareFieldOptions = {},
) {
    const active = enabled && needsKeyboardViewportCompensation();
    const [menuPlacement, setMenuPlacement] =
        useState<KeyboardMenuPlacement>(defaultMenuPlacement);
    const cleanupRef = useRef<(() => void) | null>(null);

    const getFieldElement = useCallback(() => {
        const anchor = anchorRef.current;
        return (
            anchor?.querySelector<HTMLElement>(FIELD_INPUT_SELECTOR) ??
            anchor ??
            null
        );
    }, [anchorRef]);

    const syncPlacement = useCallback(() => {
        const element = getFieldElement();

        if (!active || !menuMaxHeight || !element) {
            return;
        }

        setMenuPlacement(
            resolveKeyboardMenuPlacement(element, {
                maxHeight: menuMaxHeight,
                clearance,
            }),
        );
    }, [active, clearance, getFieldElement, menuMaxHeight]);

    const stopTracking = useCallback(() => {
        cleanupRef.current?.();
        cleanupRef.current = null;
    }, []);

    const onFieldFocus = useCallback(
        (event: { target: EventTarget | null }) => {
            if (!active || !(event.target instanceof Element)) {
                return;
            }

            const target = event.target;
            syncPlacement();
            stopTracking();

            cleanupRef.current = onKeyboardSettle(() => {
                if (scrollOnFocus) {
                    scrollIntoKeyboardView(target, { clearance });
                }
                syncPlacement();
            });
        },
        [active, clearance, scrollOnFocus, stopTracking, syncPlacement],
    );

    useEffect(() => stopTracking, [stopTracking]);

    if (!active) {
        return {
            menuPlacement: defaultMenuPlacement,
            onFieldFocus: noopFocus,
            onFieldBlur: noopFocus,
            syncPlacement: noopFocus,
        };
    }

    return {
        menuPlacement,
        onFieldFocus,
        onFieldBlur: stopTracking,
        syncPlacement,
    };
}
